import { useState } from "react";
import { usePoll } from "@inertiajs/react";
import PageHeader from "@/components/PageHeader";
import TabNavigation from "@/components/tabs/TabNavigation";
import ContractBundleModal from "@/components/modals/ContractBundleModal";
import LockAndIssueModal from "@/components/modals/LockAndIssueModal";
import Button from "@/components/buttons/Button";
import Select, { SelectOption } from "@/components/inputs/Select";
import { Artwork } from "@/components/ArtworkSlot";
import buildTrackTabs from "../trackTabs";
import ContractCard from "./ContractCard";
import { Bundle, RightsSummary } from "./types";
import FormActions from "@/components/buttons/FormActions";

const PdfPoller: React.FC = () => {
  usePoll(2000, { only: ["bundles"] });
  return null;
};

interface ContractsIndexProps {
  track: {
    id: number;
    title: string;
    artwork_attached: boolean;
  };
  artwork: Artwork;
  bundles: Bundle[];
  bundle_kind_options: SelectOption[];
  representative_options: SelectOption[];
  rights_summary: RightsSummary;
}

const Index: React.FC<ContractsIndexProps> = ({
  track,
  artwork,
  bundles,
  bundle_kind_options,
  representative_options,
  rights_summary,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [representativeByBundle, setRepresentativeByBundle] = useState<
    Record<number, string>
  >({});
  const [issuingBundleId, setIssuingBundleId] = useState<number | null>(null);

  // For now a track may only have one contract bundle, so the create action is
  // hidden once one exists (no DB-level constraint yet).
  const canCreate = bundles.length === 0;

  const anyPdfGenerating = bundles.some((bundle) =>
    bundle.contracts.some((contract) => contract.pdf_generating),
  );

  const issuingBundle =
    issuingBundleId === null
      ? null
      : (bundles.find((bundle) => bundle.id === issuingBundleId) ?? null);
  const issuingRepresentativeId = issuingBundle
    ? (representativeByBundle[issuingBundle.id] ?? "")
    : "";
  const issuingRepresentativeName =
    representative_options.find(
      (option) => option.value === issuingRepresentativeId,
    )?.label ?? "";

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      {anyPdfGenerating && <PdfPoller />}
      <PageHeader
        backLink={{ href: "/tracks", text: "Back to all tracks" }}
        title={track.title}
        primaryAction={
          canCreate
            ? {
                text: "New Contract",
                onClick: () => setIsModalOpen(true),
              }
            : undefined
        }
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <TabNavigation tabs={buildTrackTabs(track.id, "contracts")} />

      {bundles.length === 0 ? (
        <div className="dark:bg-cream-950 flex w-full flex-col items-start gap-2 rounded-2xl bg-white p-6 shadow md:p-10">
          <p className="text-cream-600 dark:text-cream-400 text-sm">
            No contracts have been created for this track yet.
          </p>
        </div>
      ) : (
        bundles.map((bundle) => {
          const blockingReasons = bundle.contracts.flatMap(
            (contract) => contract.blocking_reasons,
          );
          const representativeId = representativeByBundle[bundle.id] ?? "";
          const canIssue =
            bundle.status === "draft" &&
            blockingReasons.length === 0 &&
            representativeId !== "";

          return (
            <div key={bundle.id} className="flex w-full flex-col gap-4">
              <h2 className="text-cream-900 dark:text-cream-100 text-lg font-semibold">
                {bundle.name}
              </h2>
              {bundle.contracts.map((contract) => (
                <ContractCard
                  key={contract.id}
                  contract={contract}
                  trackId={track.id}
                />
              ))}

              {bundle.status === "draft" && (
                <div className="dark:bg-cream-950 flex w-full flex-col gap-4 rounded-2xl bg-white p-6 shadow md:p-10">
                  <h3 className="text-cream-900 dark:text-cream-100 font-semibold">
                    Prepare for signing
                  </h3>
                  <Select
                    label="Altar representative"
                    name="representative_id"
                    placeholder="Select a representative"
                    options={representative_options}
                    value={representativeId}
                    onChange={(e) =>
                      setRepresentativeByBundle((prev) => ({
                        ...prev,
                        [bundle.id]: e.target.value,
                      }))
                    }
                  />
                  {blockingReasons.length > 0 && (
                    <ul className="text-danger-600 dark:text-danger-400 list-disc pl-5 text-sm">
                      {blockingReasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  <FormActions>
                    <Button
                      text="Lock and issue"
                      type="primary"
                      htmlButtonType="button"
                      onClick={() => setIssuingBundleId(bundle.id)}
                      disabled={!canIssue}
                    />
                  </FormActions>
                </div>
              )}
            </div>
          );
        })
      )}

      <ContractBundleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        trackId={track.id}
        bundleKindOptions={bundle_kind_options}
        disabled={!canCreate}
      />

      <LockAndIssueModal
        isOpen={issuingBundle !== null}
        onClose={() => setIssuingBundleId(null)}
        issueUrl={issuingBundle?.issue_url ?? ""}
        representativeId={issuingRepresentativeId}
        representativeName={issuingRepresentativeName}
        contractTitles={
          issuingBundle?.contracts.map((contract) => contract.title) ?? []
        }
        rightsSummary={rights_summary}
      />
    </div>
  );
};

export default Index;
